<template>
  <div class="container-cap">
    <h2>Données
      <span class="label" ref="title" :class="`label-${module}`">
        {{ moduleName }}
      </span>
    </h2>

    <hr>
    <div class="row">
      <div class="col-lg-10">
        <alert type="warning" class="text-justify">
          <p>
            <strong>
              Attention ! Cette page est en cours de construction.
            </strong>
          </p>
          <p>
            N'hésitez pas à nous contacter si vous repérez des erreurs dans
            les données ou si vous avez des suggestions.
          </p>
        </alert>
        <alert type="info" class="text-justify" v-if="canEditSheets">
          <p>
            Seules les fiches <strong>validées</strong> sont inclues sur cette
            page. Pour valider vos fiches, utilisez le
            <router-link :to="`${routePrefix}/sheets`">
              <span class="glyphicon glyphicon-pencil"
                    aria-hidden="true">
              </span>
              Module de saisie
            </router-link>.
          </p>
        </alert>
      </div>
    </div>
    <hr>

    <div v-if="display">
      <div class="row">
        <div class="col-xs-12">
          <p>
            <btn type="warning" @click="refresh">
              <span class="glyphicon glyphicon-refresh"
                    aria-hidden="true">
              </span>
              Rafraîchir
            </btn>
            <btn type="primary" @click="exportCSV"
                 :disabled="empty">
              <span class="glyphicon glyphicon-download-alt"
                    aria-hidden="true">
              </span>
              Exporter toutes les fiches en CSV
            </btn>
            <save-blob :target="csv.target"></save-blob>
          </p>
        </div>
      </div>
      <hr>
      <div class="row">
        <div class="col-xs-12">
          <modal v-model="select.show" :header="false"
                 @hide="select.hide($event)" size="lg">
            <template slot="footer">
              <btn @click="select.none()">Tout déselectionner</btn>
              <btn type="success" @click="select.hide('ok')">Ok</btn>
            </template>
            <div class="row">
              <div class="select-container">
                <span v-for="(s, k) in unitsSelector"
                      :key="`${k}-selector`"
                      :class="`col-xs-12 col-sm-12`">
                  <selector :lines="s" type="dark">
                  </selector>
                </span>
              </div>
            </div>
          </modal>
        </div>
      </div>
      <div class="row">
        <div class="col-xs-12">
          <div class="statistics-filter">
            <h4 v-html="filtersTitle">
            </h4>
            <btn type="default" @click="unitFilter()"
                 :disabled="empty">
              Filtrer par Ufs
            </btn>
          </div>
          <hr>
          <tabs>
            <tab title="Statistiques">
              <div class="row">
                <div class="col-xs-12 col-lg-10">
                  <btn type="primary" @click="exportPDF"
                       size="sm"
                       :disabled="empty">
                    <span class="glyphicon glyphicon-download-alt"
                          aria-hidden="true">
                    </span>
                    Exporter en PDF
                  </btn>
                  <table class="table table-responsive module-table">
                    <tbody>
                      <tr v-for="d in details"
                          class="stat"
                          :key="`sheets-statistics-details-${d.key}`">
                        <td>{{ d.title }}</td>
                        <td>
                          <table class="table-responsive stat-table">
                            <tbody>
                              <tr v-for="(v, k) in d.values"
                                  :key="`${d.key}-${k}`">
                                <td>{{ v.label }}</td>
                                <td class="stat-value">{{ v.count }}</td>
                                <td class="stat-percent">{{ v.percentage }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </tab>
            <tab title="Graphiques">
              <div v-for="(c, i) in charts"
                   :key="c.key">
                <div class="col-xs-12 col-md-6 col-lg-5">
                  <chart :type="`${c.type}-chart`"
                         :exportConfig="c.exportConfig"
                         :config="c.config"></chart>
                </div>
                <div class="clearfix hidden-xs hidden-sm"
                     v-if="i % 2 === 1"></div>
              </div>
            </tab>
          </tabs>
        </div>
      </div>
    </div>
    <div v-else>
      <p><strong><loading-text>Chargement</loading-text></strong></p>
    </div>

  </div>
</template>
<script>
import chart from '@/sui/components/charts/chart'
import loading from '@/sui/components/loadingtext'
import selector from '@/sui/components/results/selector'
import saveblob from '@/sui/components/saveblob'
import modules from '@/sui/data/modules'
import impl from '@/views/sheet/impl/data'
import stringify from 'csv-stringify'
import moment from 'moment'
import units from '@/sui/data/units'

import { mapState, mapGetters } from 'vuex'

export default {
  name: 'sheets-data',

  data: function () {
    return {
      isMounted: false,
      csv: {
        target: null
      },

      filters: {
        uf: []
      },

      select: impl.select
    }
  },

  mounted: function () {
    this.isMounted = true
    this.select.reset()
    if (this.available[this.module] && !this.display) {
      this.refresh()
    }
  },

  computed: {
    ...mapState({
      all: state => state.sheet.all,
      available: state => state.sheet.available,
      full: state => state.sheet.full,
      fullAvailable: state => state.sheet.fullAvailable,
      denominators: state => state.denominators.edit
    }),

    ...mapGetters({
      userId: 'user/id',
      get: 'surveillances/get'
    }),

    canEditSheets: function () {
      if (this.surveillance == null ||
          this.module == null ||
          this.module === 'denominators') {
        return false
      }
      return this.surveillance.canEditSheets(this.userId, this.module)
    },

    filtersTitle: function () {
      if (this.filters.uf.length === 0) {
        return `Filtres: Aucun`
      }

      let result = `Filtres: <br /><ul>`
      this.filters.uf.forEach(
        (uf, i) => {
          result += `<li>${uf.uf} <small>(${uf.unitTypeCode})</small></li>`
        }
      )
      result += '</ul>'
      return result
    },

    surveillance: function () {
      return this.get(this.$route.params.year, this.$route.params.id)
    },

    moduleName: function () {
      return modules[this.year][this.module].label
    },

    moduleColor: function () {
      return modules[this.year][this.module].color
    },

    module: function () {
      return this.$route.params.st
    },

    year: function () {
      return this.$route.params.year
    },

    routePrefix: function () {
      const { year, id } = this.$route.params
      return `/establishments/${year}/${id}/module/${this.module}`
    },

    display: function () {
      return this.fullAvailable[this.module] && this.denominators != null
    },

    filteredFull: function () {
      if (this.filters.uf.length === 0) {
        return this.full[this.module]
      }

      const reducer = (result, s) => {
        const unit = this.filters.uf.find(
          (uf) => {
            return uf.unitType === s.f.unitType && uf.uf === s.f.uf
          }
        )

        if (unit != null) {
          result.push(s)
        }
        return result
      }
      const result = this.full[this.module].reduce(reducer, [])
      return result
    },

    empty: function () {
      return this.display && this.filteredFull.length === 0
    },

    stats: function () {
      if (!this.display) {
        return {}
      }

      const list = this.filteredFull
      if (list.length === 0) {
        return {}
      }

      return impl[this.module].stats(list)
    },

    units: function () {
      if (!this.display) {
        return []
      }

      const foundUnits = []
      this.full[this.module].forEach(
        s => {
          const code = `${s.f.uf}${s.f.unitType}`
          if (foundUnits.indexOf(code) === -1) {
            const type = units[this.year].findFromIndex(s.f.unitType)
            foundUnits[code] = {
              uf: s.f.uf,
              unitType: s.f.unitType,
              unitTypeCode: type.code,
              label: `${s.f.uf} (${type.code})`
            }
          }
        }
      )
      return foundUnits
    },

    unitsSelector: function () {
      const choices = {}
      for (let c in this.units) {
        const u = this.units[c]
        const selected = this.select.list.indexOf(u) !== -1
        const prefix = u.unitTypeCode
        if (choices[prefix] == null) {
          choices[prefix] = []
        }
        choices[prefix].push(
          {
            type: selected ? 'warning' : 'default',
            label: u.uf,
            code: u.unitTypeCode,
            toggle: () => this.select.toggle(u)
          }
        )
      }
      const lines = []
      const keys = Object.keys(choices).sort()
      keys.forEach(
        k => {
          const ch = choices[k]
          ch.sort(
            (f, s) => { return f.code.localeCompare(s.code) }
          )

          lines.push(
            {
              title: k,
              choices: ch,
              select: (i) => ch[i].toggle()
            }
          )
        }
      )

      return [lines]
    },

    charts: function () {
      if (!this.display || this.empty || this.stats.count === 0) {
        return {}
      }

      return impl[this.module].charts(this.stats)
    },

    details: function () {
      if (!this.display) {
        return []
      }

      if (this.empty) {
        return [
          {
            key: 'count',
            title: 'Nombre de fiches',
            values: [{ label: 'Aucune fiche validée' }]
          }
        ]
      }
      return impl[this.module].details(this.stats)
    }
  },

  watch: {
    available: {
      handler (values) {
        if (values[this.module] && !this.display) {
          this.refresh()
        }
      },
      deep: true
    },

    module: function () {
      this.reset()
    },

    surveillance: function () {
      this.reset()
    }
  },

  methods: {
    refresh: function () {
      this.$store.dispatch(
        'sheet/full',
        {
          t: this.module,
          y: this.surveillance.year,
          sv: this.surveillance.id,
          st: 'valid'
        }
      )

      this.$store.dispatch(
        'denominators/edit',
        {
          s: this.surveillance.id,
          y: this.surveillance.year
        }
      )
    },

    exportPDF: function () {
      impl.exportPDF(this)
    },

    exportCSV: function () {
      const module = modules[this.year][this.module]
      const values = module.sheets.flatten(
        {
          type: this.module,
          sheets: this.full[this.module],
          onlyClosed: false,
          surveillances: [ this.surveillance ],
          denominators: [ this.denominators ],
          removeEmptyColumns: module.removeEmptyColumns
        }
      )
      const vm = this
      const finess = this.surveillance.establishment.finess
      const date = moment().format('DD-MM-YYYY-hh-mm-ss')
      const filename =
        `SPIADI${this.year} - ${finess} - ${this.moduleName} - ${date}.csv`
      stringify(
        values.sheets, values.options,
        (err, output) => {
          if (err == null) {
            setTimeout(
              function () {
                vm.csv.target = {
                  filename: filename,
                  blob: new Blob([output], { type: 'text/plain' })
                }
              }, 10)
          }
        }
      )
    },

    unitFilter: function () {
      const vm = this
      this.select.showModal(
        (list) => {
          vm.filters.uf = list.slice()
        }
      )
    },

    reset: function () {
      this.filters.uf = []
      this.select.reset()
    },

    checkRoute: function (to, from, next) {
      next()
    }
  },

  components: {
    'chart': chart,
    'loading-text': loading,
    'selector': selector,
    'save-blob': saveblob
  }
}
</script>
<style scoped lang="less">
@import '~variables';
p {
  margin: 0rem;
  .btn {
    margin-right: 1rem;
  }
}

.statistics-filter {
  h4 {
    margin-top: 0rem;
  }
}

.alert:last-child {
  margin-bottom: 0rem;
}

.stat-value {
  text-align: right;
  font-weight: bold;
  width: 5rem;
}

.stat-percent {
  text-align: right;
  width: 5rem;
}

.module-table {
  margin-top: 1rem;
}

.stat-table {
  width: 100%;
}

.tab-pane {
  padding-top: 2rem;
  padding-bottom: 5rem;
}

</style>
